/**
 * Reembolsos por coorte no modo ao vivo (VEN-05 e Pauta EXP-06): taxas D+7, D+30 e D+60, chargeback do
 * mês, o gráfico de coortes semanais e o dia a dia do cartão Reembolsos, todos com os pedidos reais da
 * Kiwify (data da venda e data do reembolso que o servidor manda). Sem venda antiga o bastante, o número fica vazio ("—") com o motivo;
 * nunca o da simulação.
 */
import type { ColumnarSeries, KpiId, KpiValue, Query } from '@rota/contracts';
import {
  BRT,
  buckets,
  compareMs,
  DAY,
  dayStart,
  HOUR,
  isPaid,
  isRefundLike,
  kv,
  matches,
  noRealKpi,
  orderDates,
  paymentOf,
  type RealCtx,
  type RealModule,
  type RealOrderRow,
  rangeMs,
  revenueOf,
  todayKey,
} from './realCore';

/** Taxas de reembolso por coorte e chargeback do mês: sempre reais (no stream, a simulação não passa). */
export const REFUND_KPI_IDS = [
  'refundRateD7',
  'refundRateD30',
  'refundRateD60',
  'chargebackRateMonth',
] as const satisfies readonly KpiId[];

type CohortKpi = 'refundRateD7' | 'refundRateD30' | 'refundRateD60';
const COHORT_DAYS: Record<CohortKpi, number> = { refundRateD7: 7, refundRateD30: 30, refundRateD60: 60 };
/** Janela de vendas de cada coorte (as que completaram o prazo nos últimos 30 dias). */
const COHORT_WINDOW_DAYS = 30;
/** Semanas no gráfico de coortes (as mesmas 13 do VEN-05 no desktop). */
const COHORT_WEEKS = 13;
/** Histórico lido: 13 semanas de coorte (91 dias) e a semana corrente. */
const HISTORY_DAYS = COHORT_WEEKS * 7 + 7;
/** Coortes mudam devagar: o histórico de pedidos vale por 5 minutos. */
const HISTORY_TTL = 5 * 60_000;
/** Amostra mínima do curso para julgar a taxa (a mesma do `kpiDefs`). */
const MIN_SAMPLE = 50;

/** "14/10" no fuso de São Paulo. */
const dm = (at: number): string => {
  const d = new Date(at - BRT).toISOString();
  return `${d.slice(8, 10)}/${d.slice(5, 7)}`;
};

/** Venda do curso aprovada (paga ou reembolsada depois): a hora da aprovação. */
function frontSaleAt(o: RealOrderRow): number | null {
  if (o.kind !== 'front' || !(isPaid(o) || isRefundLike(o.status))) return null;
  return orderDates(o).paidAt;
}

/** Reembolso (ou chargeback) da venda até `days` depois da compra e até `asOf`. */
function refundedWithin(o: RealOrderRow, saleAt: number, days: number, asOf: number): boolean {
  const at = orderDates(o).refundedAt;
  return at != null && at <= asOf && at - saleAt <= days * DAY;
}

export interface CohortRate {
  /** Reembolsos ÷ vendas da coorte (null sem venda na coorte). */
  rate: number | null;
  sales: number;
  refunds: number;
  /** Vendas aprovadas de `from` até antes de `until`. */
  from: number;
  until: number;
}

/**
 * Taxa de reembolso em D+`days`: vendas do curso aprovadas entre `asOf` − (days + 30) dias e
 * `asOf` − days dias (todas já completaram o prazo), reembolsadas até `days` dias depois da compra.
 */
export function refundCohortRate(rows: readonly RealOrderRow[], asOf: number, days: number): CohortRate {
  const until = asOf - days * DAY;
  const from = until - COHORT_WINDOW_DAYS * DAY;
  let sales = 0;
  let refunds = 0;
  for (const o of rows) {
    const at = frontSaleAt(o);
    if (at == null || at < from || at >= until) continue;
    sales++;
    if (refundedWithin(o, at, days, asOf)) refunds++;
  }
  return { rate: sales ? refunds / sales : null, sales, refunds, from, until };
}

/** Início do mês de `at` em São Paulo. */
const monthStart = (at: number): number => dayStart(`${todayKey(at).slice(0, 7)}-01`);

/**
 * Chargeback do mês: chargebacks que aconteceram no mês (pela data do chargeback) ÷ vendas no cartão
 * aprovadas no mês, no fuso de São Paulo. Venda sem forma de pagamento registrada não conta como cartão.
 */
export function chargebackMonth(
  rows: readonly RealOrderRow[],
  asOf: number,
): { rate: number | null; chargebacks: number; cardSales: number; from: number } {
  // `asOf` pode ser a meia-noite que fecha o último dia: o mês é o desse dia.
  const from = monthStart(asOf - 1);
  let chargebacks = 0;
  let cardSales = 0;
  for (const o of rows) {
    const { paidAt, refundedAt } = orderDates(o);
    const sold = isPaid(o) || isRefundLike(o.status);
    if (
      sold &&
      paidAt != null &&
      paidAt >= from &&
      paidAt < asOf &&
      o.payment &&
      paymentOf(o.payment) === 'credit_card'
    )
      cardSales++;
    if (
      o.status !== 'refunded' &&
      isRefundLike(o.status) &&
      refundedAt != null &&
      refundedAt >= from &&
      refundedAt < asOf
    )
      chargebacks++;
  }
  return { rate: cardSales ? chargebacks / cardSales : null, chargebacks, cardSales, from };
}

/** Segunda-feira (00:00 em São Paulo) da semana de `at`. */
function mondayOf(at: number): number {
  const start = dayStart(todayKey(at));
  const wd = new Date(start - BRT).getUTCDay();
  return start - ((wd + 6) % 7) * DAY;
}

/**
 * Coortes semanais (VEN-05): por semana de venda do curso, a taxa de reembolso em D+7, D+30 e D+60.
 * Prazo ainda não completado fica vazio; as semanas antes da 1ª venda real saem do gráfico.
 */
export function refundCohortSeries(
  rows: readonly RealOrderRow[],
  asOf: number,
): { t: number[]; d7: (number | null)[]; d30: (number | null)[]; d60: (number | null)[] } {
  const last = mondayOf(asOf - 1);
  const weeks = Array.from({ length: COHORT_WEEKS }, (_, i) => last - (COHORT_WEEKS - 1 - i) * 7 * DAY);
  const sales = weeks.map(() => [] as { o: RealOrderRow; at: number }[]);
  for (const o of rows) {
    const at = frontSaleAt(o);
    if (at == null) continue;
    const i = weeks.findIndex((ws) => at >= ws && at < ws + 7 * DAY);
    if (i >= 0) sales[i]?.push({ o, at });
  }
  const rate = (days: number) =>
    weeks.map((ws, i) => {
      const list = sales[i] ?? [];
      // A semana só entra quando todas as vendas dela completaram o prazo.
      if (ws + 7 * DAY + days * DAY > asOf || list.length === 0) return null;
      return list.filter((s) => refundedWithin(s.o, s.at, days, asOf)).length / list.length;
    });
  const first = sales.findIndex((s) => s.length > 0);
  const from = first < 0 ? weeks.length : first;
  return {
    t: weeks.slice(from),
    d7: rate(7).slice(from),
    d30: rate(30).slice(from),
    d60: rate(60).slice(from),
  };
}

/**
 * Motivo do número vazio, em português simples. O VEN-05 junta os prazos com o mesmo motivo numa linha
 * só ("D+30 e D+60: ainda sem..."): o texto é igual para os três prazos.
 */
const COHORT_MISSING =
  'Ainda sem vendas do curso antigas o bastante para medir. A taxa aparece quando a primeira venda completar o prazo';

/** KPIs de reembolso por coorte e chargeback do mês, sobre o histórico real (filtros aplicados). */
export function refundKpis(
  rows: readonly RealOrderRow[],
  asOf: number,
  now: number,
  fresh: KpiValue['freshness'],
): Record<(typeof REFUND_KPI_IDS)[number], KpiValue> {
  const out = {} as Record<(typeof REFUND_KPI_IDS)[number], KpiValue>;
  for (const id of Object.keys(COHORT_DAYS) as CohortKpi[]) {
    const days = COHORT_DAYS[id];
    const c = refundCohortRate(rows, asOf, days);
    out[id] =
      c.rate == null
        ? noRealKpi(id, COHORT_MISSING)
        : {
            ...kv(id, c.rate, null, now),
            freshness: fresh,
            sample: { n: c.sales, min: MIN_SAMPLE, unit: 'vendas' },
            note: `${c.refunds} de ${c.sales} vendas do curso de ${dm(c.from)} a ${dm(c.until - 1)} reembolsadas em até ${days} dias`,
          };
  }
  const cb = chargebackMonth(rows, asOf);
  out.chargebackRateMonth =
    cb.rate == null
      ? noRealKpi('chargebackRateMonth', 'Nenhuma venda no cartão no mês ainda')
      : {
          ...kv('chargebackRateMonth', cb.rate, null, now),
          freshness: fresh,
          note: `${cb.chargebacks} de ${cb.cardSales} vendas no cartão no mês`,
        };
  return out;
}

/**
 * Reembolsos feitos de `from` a `to` (filtros aplicados), com `at` = data do reembolso: a mesma conta do
 * cartão Reembolsos. Servidor antigo (sem a lista própria): pela data da compra, como os totais.
 */
async function refundsByDate(ctx: RealCtx, q: Query, from: number, to: number): Promise<RealOrderRow[]> {
  const list = (await ctx.loadRefunds?.(from, to)) ?? null;
  if (!list) return (await ctx.load(from, to)).filter((o) => isRefundLike(o.status) && matches(o, q));
  return list
    .filter((o) => isRefundLike(o.status) && matches(o, q))
    .map((o) => ({ ...o, at: orderDates(o).refundedAt ?? o.at }));
}

/** Módulo do modo ao vivo: KPIs de coorte, gráfico de coortes e detalhe da métrica sem simulação. */
export function refundsModule(ctx: RealCtx): RealModule {
  const cache = new Map<number, { at: number; p: Promise<RealOrderRow[]> }>();
  /** Pedidos das últimas ~14 semanas até `asOf` (cache curto: as coortes mudam devagar). */
  const history = (asOf: number): Promise<RealOrderRow[]> => {
    const key = Math.floor(asOf / HISTORY_TTL);
    const hit = cache.get(key);
    if (hit && ctx.clock() - hit.at < HISTORY_TTL) return hit.p;
    const p = ctx.load(Math.floor((asOf - HISTORY_DAYS * DAY) / HOUR) * HOUR, asOf);
    cache.clear();
    cache.set(key, { at: ctx.clock(), p });
    p.catch(() => cache.delete(key));
    return p;
  };
  const rowsFor = async (q: Query, asOf: number) => (await history(asOf)).filter((o) => matches(o, q));

  return {
    kpiIds: REFUND_KPI_IDS,
    neverSimKpis: REFUND_KPI_IDS,
    async kpis({ q, now, r }) {
      try {
        const out = refundKpis(await rowsFor(q, r.to), r.to, now, ctx.kiwiFresh(now));
        // Filtro de produto sem o curso: a taxa por coorte é das vendas do curso (diz isso, não "sem venda").
        const p = q.filters.products;
        if (p?.length && !p.includes('front'))
          for (const id of Object.keys(COHORT_DAYS) as CohortKpi[])
            out[id] = noRealKpi(
              id,
              'A taxa por coorte é só das vendas do curso, que este filtro de produto deixa de fora',
            );
        return out;
      } catch {
        // Sem o histórico: vazio com o motivo (nunca o número da simulação, nem derrubar o snapshot).
        return Object.fromEntries(
          REFUND_KPI_IDS.map((id) => [id, noRealKpi(id, 'Não foi possível ler os pedidos antigos agora')]),
        );
      }
    },
    async series(q) {
      const now = ctx.clock();
      if (q.key === 'refundCohorts') {
        const r = rangeMs(q, now);
        const c = refundCohortSeries(await rowsFor(q, r.to), r.to);
        const series: ColumnarSeries['series'] = (['d7', 'd30', 'd60'] as const).map((k, i) => ({
          key: k,
          label: `D+${k.slice(1)}`,
          colorSlot: i + 1,
          role: 'split' as const,
          values: c[k],
        }));
        return { t: c.t, series, markers: [], freshness: ctx.kiwiFresh(now) };
      }
      // Detalhe do cartão Reembolsos (valor e quantidade): por dia ou hora do reembolso, como o número.
      if (
        (q.key === 'kpiByDay' || q.key === 'kpiByHour') &&
        (q.kpi === 'refundsAmount' || q.kpi === 'refundsCount')
      ) {
        const r = rangeMs(q, now);
        const cr = compareMs(q, now);
        const size = q.key === 'kpiByDay' ? DAY : HOUR;
        const cEnd = cr ? cr.end : 0;
        const pick = q.kpi === 'refundsAmount' ? revenueOf : () => 1;
        const [cur, cmp] = await Promise.all([
          refundsByDate(ctx, q, r.from, r.to),
          cr ? refundsByDate(ctx, q, cr.from, cEnd) : Promise.resolve(null),
        ]);
        const main = buckets(cur, r.from, r.end, size, now, pick);
        const series: ColumnarSeries['series'] = [
          { key: q.kpi, label: 'No período', role: 'primary', values: main.values },
        ];
        if (cmp && cr)
          series.push({
            key: `${q.kpi}_cmp`,
            label: 'Comparação',
            role: 'compare',
            values: buckets(cmp, cr.from, cEnd, size, Number.POSITIVE_INFINITY, pick).values,
          });
        return { t: main.t, series, markers: [], freshness: ctx.kiwiFresh(now) };
      }
      // Detalhe da métrica: a taxa por coorte não tem série por hora ou por dia (fica vazia).
      if (
        (q.key === 'kpiByDay' || q.key === 'kpiByHour') &&
        q.kpi &&
        REFUND_KPI_IDS.includes(q.kpi as never)
      ) {
        const r = rangeMs(q, now);
        const size = q.key === 'kpiByDay' ? DAY : HOUR;
        const axis = buckets([], r.from, r.end, size, now, () => 0).t;
        return {
          t: axis,
          series: [{ key: q.kpi, label: 'No período', role: 'primary', values: axis.map(() => null) }],
          markers: [],
          freshness: [],
        };
      }
      return undefined;
    },
  };
}
